import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";

interface PincodeManagerProps {
	pincodes: string[];
	onChange: (pincodes: string[]) => void;
}

export const PincodeManager: React.FC<PincodeManagerProps> = ({
	pincodes,
	onChange,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [showBulkUpload, setShowBulkUpload] = useState(false);
	const [bulkUploadText, setBulkUploadText] = useState("");
	const [showBulkDelete, setShowBulkDelete] = useState(false);
	const [bulkDeleteText, setBulkDeleteText] = useState("");

	const { toast } = useToast();
	const itemsPerPage = 20;

	// Filter pincodes based on search term (searching within the selected list for management)
    // Note: In EntityTable, search is for *adding* new ones from server.
    // Here, since we manage a bespoke list, search helps find items in the large list.
    // Also providing an input to Add.

    // Combining "Add" and "Search" might be confusing if we strictly follow EntityTable structure
    // but EntityTable's structure is: Active List (chips) + Add Search (popover).

    // Let's stick to:
    // 1. Chip List (paginated).
    // 2. "Add Pincode" input (Enter to add).
    // 3. Bulk Actions.

	const filteredPincodes = pincodes.filter(p => p.includes(searchTerm));

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentPincodes = filteredPincodes.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(filteredPincodes.length / itemsPerPage);

    // Reset page on search
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

	const handleAddPincode = (pincodeToAdd: string) => {
        const trimmed = pincodeToAdd.trim();
        if (!/^\d{6}$/.test(trimmed)) {
            toast({
                title: "Invalid Pincode",
                description: "Pincode must be a 6-digit number",
                variant: "destructive"
            });
            return;
        }
        if (pincodes.includes(trimmed)) {
            toast({
                title: "Duplicate Pincode",
                description: "This pincode is already in the list",
                variant: "destructive"
            });
            return;
        }
        onChange([...pincodes, trimmed]);
        setSearchTerm(""); // Clear search to show the added one? Or keep it?
	};

	const handleRemovePincode = (pincodeToRemove: string) => {
		onChange(pincodes.filter((p) => p !== pincodeToRemove));
	};

	const handleBulkUpload = () => {
		const newPincodes = bulkUploadText
			.split(/[\s,]+/)
			.map((p) => p.trim())
			.filter((p) => /^\d{6}$/.test(p));

		const uniqueNewPincodes = newPincodes.filter((p) => !pincodes.includes(p));

		if (uniqueNewPincodes.length > 0) {
            // Check for 1000 limit
            if (pincodes.length + uniqueNewPincodes.length > 1000) {
                 toast({
                    title: "Limit Exceeded",
                    description: `Cannot add ${uniqueNewPincodes.length} pincodes. Total would exceed 1000.`,
                    variant: "destructive",
                });
                return;
            }

			onChange([...pincodes, ...uniqueNewPincodes]);
			toast({
				title: "Success",
				description: `Added ${uniqueNewPincodes.length} pincodes`,
			});
			setBulkUploadText("");
			setShowBulkUpload(false);
		} else {
			toast({
				title: "No new pincodes",
				description: "All valid pincodes were already in the list",
                variant: "destructive"
			});
		}
	};

	const handleBulkDelete = () => {
		const pincodesToDelete = bulkDeleteText
			.split(/[\s,]+/)
			.map((p) => p.trim());

		const newPincodes = pincodes.filter((p) => !pincodesToDelete.includes(p));
        const removedCount = pincodes.length - newPincodes.length;

		if (removedCount > 0) {
			onChange(newPincodes);
			toast({
				title: "Success",
				description: `Removed ${removedCount} pincodes`,
			});
			setBulkDeleteText("");
			setShowBulkDelete(false);
		} else {
             toast({
				title: "No pincodes removed",
				description: "None of the entered pincodes were found in the list",
                variant: "destructive"
			});
        }
	};

	return (
		<div className="space-y-4 border rounded-md p-4">
            <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Selected Pincodes ({pincodes.length})</h4>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBulkUpload(true)}
                        type="button"
                    >
                        Bulk Upload
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBulkDelete(true)}
                        type="button"
                    >
                        Bulk Delete
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onChange([])}
                        type="button"
                    >
                        Clear All
                    </Button>
                </div>
            </div>

            {/* Chip List */}
			<div className="flex flex-wrap gap-2 min-h-[100px] content-start bg-gray-50 dark:bg-gray-800 p-2 rounded-md">
				{currentPincodes.length === 0 ? (
                    <div className="text-gray-500 text-sm w-full text-center py-4">No pincodes found</div>
                ) : (
                    currentPincodes.map((code) => (
                        <div
                            key={code}
                            className="flex items-center gap-1 bg-white dark:bg-gray-700 border px-2 py-1 rounded-full text-sm"
                        >
                            <span>{code}</span>
                            <button
                                type="button"
                                onClick={() => handleRemovePincode(code)}
                                className="text-gray-500 hover:text-red-500 ml-1"
                            >
                                ×
                            </button>
                        </div>
                    ))
                )}
			</div>

            {/* Pagination */}
			{totalPages > 1 && (
				<div className="flex justify-between items-center text-sm">
					<div>
						Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredPincodes.length)} of {filteredPincodes.length}
					</div>
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
							disabled={currentPage === 1}
                            type="button"
						>
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
							disabled={currentPage === totalPages}
                            type="button"
						>
							Next
						</Button>
					</div>
				</div>
			)}

            {/* Add / Search Input */}
            <div className="flex gap-2">
                <Input
                    placeholder="Type pincode and press Enter to add, or search existing..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            // If it's 6 digits, assume they want to ADD it
                            if (/^\d{6}$/.test(searchTerm)) {
                                handleAddPincode(searchTerm);
                            }
                        }
                    }}
                />
            </div>
            <div className="text-xs text-muted-foreground">
                Tip: Type a 6-digit pincode and press Enter to add it. Use the search to filter the view.
            </div>

            {/* Bulk Upload Modal */}
            <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Bulk Upload Pincodes</DialogTitle>
                        <DialogDescription>Add multiple pincodes separated by commas or spaces.</DialogDescription>
                    </DialogHeader>
                    <textarea
                         value={bulkUploadText}
                         onChange={(e) => {
                            const numbersOnly = e.target.value.replace(/[^\d,]/g, '');
                            const formatted = numbersOnly.replace(/(\d{6})(?=\d)/g, '$1,');
                            setBulkUploadText(formatted);
                         }}
                         className="w-full h-32 p-2 border rounded-md text-black"
                         placeholder="110001, 110002 110003..."
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkUpload(false)}>Cancel</Button>
                        <Button onClick={handleBulkUpload}>Add Pincodes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

             {/* Bulk Delete Modal */}
             <Dialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Bulk Delete Pincodes</DialogTitle>
                         <DialogDescription>Remove multiple pincodes by pasting them below.</DialogDescription>
                    </DialogHeader>
                    <textarea
                         value={bulkDeleteText}
                         onChange={(e) => {
                            const numbersOnly = e.target.value.replace(/[^\d,]/g, '');
                            const formatted = numbersOnly.replace(/(\d{6})(?=\d)/g, '$1,');
                            setBulkDeleteText(formatted);
                         }}
                         className="w-full h-32 p-2 border rounded-md text-black"
                         placeholder="110001, 110002..."
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDelete(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleBulkDelete}>Remove Pincodes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
		</div>
	);
};
